import {
  getSnackbarPlacement
} from "./chunk-WWJVOGE2.mjs";
import {
  findSnackbar,
  getSnackbarElevation,
  getSnackbarPosition
} from "./chunk-6FDAA7GW.mjs";

// src/snackbar.tsx
import * as React from "react";
import Box from "@mui/material/Box";
import { Alert, AlertIcon, AlertMessage } from "@clara/alert";
import { CloseButton } from "@clara/close-button";
import { jsx, jsxs } from "react/jsx-runtime";
var Snackbar = (props) => {
  const {
    position,
    message,
    status,
    id,
    action: actionProp,
    isClosable,
    onClose
  } = props;
  const elevation = getSnackbarElevation(position);
  const ids = id ? {
    root: `snackbar-${id}`,
    description: `snackbar-${id}-description`
  } : void 0;
  const action = React.isValidElement(actionProp) ? React.cloneElement(actionProp, {
    marginInline: 1
  }) : null;
  return /* @__PURE__ */ jsxs(
    Alert,
    {
      status,
      id: ids == null ? void 0 : ids.root,
      variant: "notification",
      elevation,
      width: "auto",
      children: [
        /* @__PURE__ */ jsx(AlertIcon, {}),
        /* @__PURE__ */ jsx(Box, { flex: 1, maxWidth: "100%", children: message ? /* @__PURE__ */ jsx(AlertMessage, { marginRight: 2, id: ids == null ? void 0 : ids.description, children: message }) : null }),
        /* @__PURE__ */ jsxs(Box, { display: "flex", alignItems: "center", children: [
          action,
          isClosable && /* @__PURE__ */ jsx(CloseButton, { onClick: onClose, sx: { marginInlineStart: 1 } })
        ] })
      ]
    }
  );
};
Snackbar.displayName = "Snackbar";
function createRenderSnackbar(options = {}) {
  const { render, snackbarComponent: SnackbarComponent = Snackbar } = options;
  const renderSnackbar = (props) => {
    if (typeof render === "function") {
      return render({ ...props, ...options });
    }
    return /* @__PURE__ */ jsx(SnackbarComponent, { ...props, ...options });
  };
  return renderSnackbar;
}
function createSnackbarFn(dir, defaultOptions) {
  const normalizeOptions = (options) => {
    var _a;
    return {
      ...defaultOptions,
      ...options,
      position: getSnackbarPlacement(
        (_a = options == null ? void 0 : options.position) != null ? _a : defaultOptions == null ? void 0 : defaultOptions.position,
        dir
      )
    };
  };
  const snackbar = (options) => {
    const normOptions = normalizeOptions(options);
    const Message = createRenderSnackbar(normOptions);
    return snackbarStore.notify(Message, normOptions);
  };
  snackbar.update = (id, options) => {
    snackbarStore.update(id, normalizeOptions(options));
  };
  snackbar.closeAll = snackbarStore.closeAll;
  snackbar.close = snackbarStore.close;
  snackbar.isActive = snackbarStore.isActive;
  return snackbar;
}

// src/snackbar.store.ts
var initialState = {
  top: [],
  "top-left": [],
  "top-right": [],
  "bottom-left": [],
  bottom: [],
  "bottom-right": []
};
var snackbarStore = createStore(initialState);
function createStore(initialState2) {
  let state = initialState2;
  const listeners = /* @__PURE__ */ new Set();
  const setState = (setStateFn) => {
    state = setStateFn(state);
    listeners.forEach((l) => l());
  };
  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        setState(() => initialState2);
        listeners.delete(listener);
      };
    },
    removeSnackbar: (id, position) => {
      setState((prevState) => ({
        ...prevState,
        // eslint-disable-next-line eqeqeq
        [position]: prevState[position].filter((snackbar) => snackbar.id != id)
      }));
    },
    notify: (message, options) => {
      const snackbar = createSnackbar(message, options);
      const { position, id } = snackbar;
      setState((prevSnackbars) => {
        var _a, _b;
        const isTop = position.includes("top");
        const snackbars = isTop ? [snackbar, ...(_a = prevSnackbars[position]) != null ? _a : []] : [...(_b = prevSnackbars[position]) != null ? _b : [], snackbar];
        return {
          ...prevSnackbars,
          [position]: snackbars
        };
      });
      return id;
    },
    update: (id, options) => {
      if (!id)
        return;
      setState((prevState) => {
        const nextState = { ...prevState };
        const { position, index } = findSnackbar(nextState, id);
        if (position && index !== -1) {
          nextState[position][index] = {
            ...nextState[position][index],
            ...options,
            message: createRenderSnackbar(options)
          };
        }
        return nextState;
      });
    },
    closeAll: ({ positions } = {}) => {
      setState((prev) => {
        const allPositions = [
          "bottom",
          "bottom-right",
          "bottom-left",
          "top",
          "top-left",
          "top-right"
        ];
        const positionsToClose = positions != null ? positions : allPositions;
        return positionsToClose.reduce(
          (acc, position) => {
            acc[position] = prev[position].map((snackbar) => ({
              ...snackbar,
              requestClose: true
            }));
            return acc;
          },
          { ...prev }
        );
      });
    },
    close: (id) => {
      setState((prevState) => {
        const position = getSnackbarPosition(prevState, id);
        if (!position)
          return prevState;
        return {
          ...prevState,
          [position]: prevState[position].map((snackbar) => {
            if (snackbar.id == id) {
              return {
                ...snackbar,
                requestClose: true
              };
            }
            return snackbar;
          })
        };
      });
    },
    isActive: (id) => Boolean(findSnackbar(snackbarStore.getState(), id).position)
  };
}
var counter = 0;
function createSnackbar(message, options = {}) {
  var _a, _b;
  counter += 1;
  const id = (_a = options.id) != null ? _a : counter;
  const position = (_b = options.position) != null ? _b : "bottom";
  return {
    id,
    message,
    position,
    duration: options.duration,
    onCloseComplete: options.onCloseComplete,
    onRequestRemove: () => snackbarStore.removeSnackbar(String(id), position),
    status: options.status,
    requestClose: false,
    containerStyle: options.containerStyle
  };
}

export {
  snackbarStore,
  createRenderSnackbar,
  createSnackbarFn
};
